import { Theme } from '@eversports/react-components/design-tokens/theme'
import CircularProgress from '@eversports/react-components/primitives/CircularProgress'
import { VerticalFlex } from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Placeholder from '@eversports/react-components/primitives/Placeholder'
import Text from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'

import { PaymentElement } from '@stripe/react-stripe-js'
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js'
import { Localized } from '../../localization/react'
import BackButton from '../BackButton'

interface Props {
  isPaymentProcessing: boolean
  isOrderConfirmed: boolean
  handleSelectedPaymentMethodChange: ({
    isValid,
    paymentMethodId,
  }: {
    isValid: boolean
    paymentMethodId?: string
  }) => void
}

const useStyles = makeStyles((theme: Theme) => ({
  overlay: {
    position: 'absolute',
    zIndex: 100,
    height: '100%',
    width: '100%',
  },
  paymentFormWrapper: ({ isPaymentProcessing }: Pick<Props, 'isPaymentProcessing'>) => ({
    display: isPaymentProcessing ? 'none' : 'block',
  }),
  container: {
    position: 'relative',
    marginBottom: theme.layoutSpacing.hero,
  },
}))

const PaymentForm = ({ isPaymentProcessing, isOrderConfirmed, handleSelectedPaymentMethodChange }: Props) => {
  const classes = useStyles({ isPaymentProcessing })

  const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
    const { complete, value } = event

    handleSelectedPaymentMethodChange({
      isValid: complete,
      paymentMethodId: value.payment_method?.id,
    })
  }

  return (
    <div className={classes.container} data-testid="payment-options-container">
      {isPaymentProcessing && (
        <div className={classes.overlay}>
          <Placeholder height={200} width="100%" center>
            <VerticalFlex verticalSpacing="default" alignItems="center">
              <CircularProgress />
              <Text data-testid="payment-options-payment-processing">
                <Localized id="processing-payment" />
              </Text>
            </VerticalFlex>
          </Placeholder>
        </div>
      )}
      <div className={classes.paymentFormWrapper}>
        <VerticalFlex verticalSpacing="wide">
          <Heading is="h2" textTransform="uppercase">
            <Localized id="payment-heading" />
          </Heading>
          <PaymentElement
            options={{ layout: 'accordion', fields: { billingDetails: { email: 'never', name: 'never' } } }}
            onChange={handlePaymentElementChange}
          />
          {(!isOrderConfirmed || !isPaymentProcessing) && <BackButton abortOrderOnBackClick />}
        </VerticalFlex>
      </div>
    </div>
  )
}

export default PaymentForm
